import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Send, Loader2, Dog, Briefcase, MapPin, ArrowRight, Mic, Mail, Phone, Check, X, Bell } from 'lucide-react';

const EXAMPLES = {
  szczeniaki: ['Golden Retriever do 4000 zł koło Warszawy', 'Mały pies rodzinny, mazowieckie', 'Border Collie z rodowodem'],
  firmy: ['Groomer w Krakowie', 'Hotel dla psów, Gdańsk', 'Weterynarz blisko mnie'],
};

const ResultCard = ({ r, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(r.url)}
    data-testid={`concierge-result-${r.slug}`}
    className="group flex-shrink-0 w-60 text-left rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-orange-400/40 hover:-translate-y-0.5 transition-all"
  >
    <div className="h-32 bg-[#0b1424] relative overflow-hidden">
      {r.image ? (
        <img src={r.image} alt={r.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#12233a] to-[#0b1424]">
          {r.type === 'firma' ? <Briefcase className="w-9 h-9 text-orange-300/40" /> : <Dog className="w-9 h-9 text-orange-300/40" />}
        </div>
      )}
    </div>
    <div className="p-3.5">
      <p className="font-bold text-white text-sm truncate">{r.name}</p>
      <p className="text-xs text-orange-300/90 truncate mt-0.5">{r.subtitle}</p>
      {r.location && (
        <p className="text-[11px] text-white/45 mt-1.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.location}</p>
      )}
      <span className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-white group-hover:text-orange-300 transition-colors">
        Zobacz profil <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </div>
  </button>
);

export default function CatalogConcierge({ defaultMode = 'szczeniaki', lockMode }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(lockMode || defaultMode);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [listening, setListening] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [channel, setChannel] = useState('whatsapp');
  const [contact, setContact] = useState('');
  const [sendState, setSendState] = useState(null); // null | 'sending' | { ok, message }
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [subEmail, setSubEmail] = useState('');
  const [subConsent, setSubConsent] = useState(false);
  const [subState, setSubState] = useState(null); // null | 'sending' | { ok, message }
  const recognitionRef = useRef(null);
  const voiceSupported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const run = async (text) => {
    const query = (text ?? q).trim();
    if (!query || loading) return;
    setQ(query);
    setLoading(true);
    setRes(null);
    setShowSend(false);
    setSendState(null);
    setShowSubscribe(false);
    setSubState(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concierge/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, mode }),
      });
      const d = await r.json();
      setRes(d && typeof d === 'object' ? { message: d.message || '', results: d.results || [] } : { message: 'Spróbuj ponownie.', results: [] });
    } catch {
      setRes({ message: 'Nie udało się połączyć. Spróbuj ponownie.', results: [] });
    } finally {
      setLoading(false);
    }
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const rec = new SR();
    rec.lang = 'pl-PL';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript || '';
      if (transcript) { setQ(transcript); run(transcript); }
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  };

  const switchMode = (m) => { setMode(m); setRes(null); setShowSend(false); setSendState(null); setShowSubscribe(false); setSubState(null); };

  const sendOffers = async () => {
    const c = contact.trim();
    if (!c || sendState === 'sending') return;
    setSendState('sending');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concierge/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel,
          contact: c,
          query: q,
          items: (res?.results || []).map((x) => ({ name: x.name, url: x.url })),
        }),
      });
      const d = await r.json();
      setSendState({ ok: !!d.ok, message: d.message || (d.ok ? 'Wysłano!' : 'Nie udało się wysłać.') });
    } catch {
      setSendState({ ok: false, message: 'Nie udało się połączyć. Spróbuj ponownie.' });
    }
  };

  const subscribe = async () => {
    const email = subEmail.trim();
    if (!email || !subConsent || subState === 'sending') return;
    setSubState('sending');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concierge/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, query: q, mode, consent: subConsent }),
      });
      const d = await r.json();
      setSubState({ ok: !!d.ok, message: d.message || (d.ok ? 'Zapisano!' : 'Nie udało się zapisać.') });
    } catch {
      setSubState({ ok: false, message: 'Nie udało się połączyć. Spróbuj ponownie.' });
    }
  };

  return (
    <div
      className="rounded-2xl border border-orange-400/20 bg-gradient-to-br from-orange-500/[0.07] via-white/[0.02] to-transparent p-4 sm:p-5"
      data-testid="concierge-root"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-orange-500 text-white flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <p className="font-bold text-white text-[15px] leading-tight">Asystent APIS — opisz, czego szukasz</p>
            <p className="text-xs text-white/50 leading-tight mt-0.5">Napisz naturalnie, a dobierzemy gotowe oferty z katalogu.</p>
          </div>
        </div>
        {!lockMode && (
        <div className="sm:ml-auto inline-flex rounded-xl bg-black/30 border border-white/10 p-1 flex-shrink-0" data-testid="concierge-mode-toggle">
          <button
            type="button" onClick={() => switchMode('szczeniaki')} data-testid="concierge-mode-szczeniaki"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${mode === 'szczeniaki' ? 'bg-orange-500 text-white' : 'text-white/60 hover:text-white'}`}
          >
            <Dog className="w-3.5 h-3.5" /> Szczeniaki / Hodowle
          </button>
          <button
            type="button" onClick={() => switchMode('firmy')} data-testid="concierge-mode-firmy"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${mode === 'firmy' ? 'bg-orange-500 text-white' : 'text-white/60 hover:text-white'}`}
          >
            <Briefcase className="w-3.5 h-3.5" /> Firmy / Usługi
          </button>
        </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="flex-1 relative">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            data-testid="concierge-input"
            placeholder={mode === 'szczeniaki' ? 'np. Golden Retriever do 4000 zł koło Warszawy' : 'np. groomer w Krakowie'}
            className={`w-full h-12 rounded-xl pl-4 ${voiceSupported ? 'pr-12' : 'pr-4'} bg-black/30 border border-white/10 text-white placeholder-white/35 text-sm focus:outline-none focus:border-orange-400`}
          />
          {voiceSupported && (
            <button
              type="button" onClick={startVoice} data-testid="concierge-mic" title="Wyszukaj głosem"
              className={`absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center transition ${listening ? 'bg-red-500 text-white animate-pulse' : 'text-white/50 hover:text-orange-300 hover:bg-white/5'}`}
            >
              <Mic className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => run()}
          disabled={loading || !q.trim()}
          data-testid="concierge-submit"
          className="h-12 inline-flex items-center justify-center gap-2 px-6 rounded-xl bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold transition disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Szukaj
        </button>
      </div>

      {!res && !loading && (
        <div className="mt-3 flex flex-wrap gap-2">
          {EXAMPLES[mode].map((ex, i) => (
            <button
              key={ex} type="button" onClick={() => run(ex)} data-testid={`concierge-example-${i}`}
              className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white transition"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-white/60" data-testid="concierge-loading">
          <Loader2 className="w-4 h-4 animate-spin text-orange-400" /> Analizuję i szukam ofert…
        </div>
      )}

      {res && (
        <div className="mt-4">
          <p className="text-sm text-white/80 mb-3" data-testid="concierge-message">{res.message}</p>
          {res.results.length > 0 && (
            <>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="concierge-results">
                {res.results.map((r) => <ResultCard key={r.slug || r.name} r={r} onOpen={(u) => navigate(u)} />)}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <button
                  type="button"
                  onClick={() => { setShowSend((v) => !v); setShowSubscribe(false); setSendState(null); }}
                  data-testid="concierge-send-toggle"
                  className={`inline-flex items-center gap-2 text-sm font-semibold transition ${showSend ? 'text-orange-200' : 'text-orange-300 hover:text-orange-200'}`}
                >
                  <Send className="w-4 h-4" /> Wyślij te oferty do siebie
                </button>
                <button
                  type="button"
                  onClick={() => { setShowSubscribe((v) => !v); setShowSend(false); setSubState(null); }}
                  data-testid="concierge-subscribe-toggle"
                  className={`inline-flex items-center gap-2 text-sm font-semibold transition ${showSubscribe ? 'text-orange-200' : 'text-white/70 hover:text-white'}`}
                >
                  <Bell className="w-4 h-4" /> Powiadom mnie o nowych
                </button>
              </div>

              {showSend && (
                <div className="mt-3 rounded-xl border border-white/10 bg-black/25 p-3.5" data-testid="concierge-send-panel">
                  {sendState && typeof sendState === 'object' ? (
                    <div
                      data-testid="concierge-send-result"
                      className={`flex items-center gap-2 text-sm font-medium ${sendState.ok ? 'text-emerald-300' : 'text-red-300'}`}
                    >
                      {sendState.ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />} {sendState.message}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2 mb-2.5">
                        <p className="text-xs font-semibold text-white/70">Dokąd wysłać znalezione oferty?</p>
                        <div className="inline-flex rounded-lg bg-black/30 border border-white/10 p-0.5" data-testid="concierge-channel-toggle">
                          <button
                            type="button" onClick={() => setChannel('whatsapp')} data-testid="concierge-channel-whatsapp"
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition ${channel === 'whatsapp' ? 'bg-orange-500 text-white' : 'text-white/60 hover:text-white'}`}
                          >
                            <Phone className="w-3 h-3" /> WhatsApp
                          </button>
                          <button
                            type="button" onClick={() => setChannel('email')} data-testid="concierge-channel-email"
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition ${channel === 'email' ? 'bg-orange-500 text-white' : 'text-white/60 hover:text-white'}`}
                          >
                            <Mail className="w-3 h-3" /> E-mail
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type={channel === 'email' ? 'email' : 'tel'}
                          value={contact}
                          onChange={(e) => setContact(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') sendOffers(); }}
                          data-testid="concierge-contact-input"
                          placeholder={channel === 'email' ? 'twoj@email.pl' : '+48 600 100 200'}
                          className="flex-1 h-11 rounded-lg px-3.5 bg-black/30 border border-white/10 text-white placeholder-white/35 text-sm focus:outline-none focus:border-orange-400"
                        />
                        <button
                          type="button"
                          onClick={sendOffers}
                          disabled={!contact.trim() || sendState === 'sending'}
                          data-testid="concierge-send-btn"
                          className="h-11 inline-flex items-center justify-center gap-2 px-5 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold transition disabled:opacity-50"
                        >
                          {sendState === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Wyślij
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {showSubscribe && (
                <div className="mt-3 rounded-xl border border-white/10 bg-black/25 p-3.5" data-testid="concierge-subscribe-panel">
                  {subState && typeof subState === 'object' ? (
                    <div
                      data-testid="concierge-subscribe-result"
                      className={`flex items-center gap-2 text-sm font-medium ${subState.ok ? 'text-emerald-300' : 'text-red-300'}`}
                    >
                      {subState.ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />} {subState.message}
                    </div>
                  ) : (
                    <>
                      <p className="text-xs font-semibold text-white/70 mb-2.5">
                        Wyślemy Ci e-mail, gdy pojawią się nowe oferty pasujące do Twojego zapytania.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="email"
                          value={subEmail}
                          onChange={(e) => setSubEmail(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') subscribe(); }}
                          data-testid="concierge-subscribe-email"
                          placeholder="twoj@email.pl"
                          className="flex-1 h-11 rounded-lg px-3.5 bg-black/30 border border-white/10 text-white placeholder-white/35 text-sm focus:outline-none focus:border-orange-400"
                        />
                        <button
                          type="button"
                          onClick={subscribe}
                          disabled={!subEmail.trim() || !subConsent || subState === 'sending'}
                          data-testid="concierge-subscribe-btn"
                          className="h-11 inline-flex items-center justify-center gap-2 px-5 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold transition disabled:opacity-50"
                        >
                          {subState === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Zapisz
                        </button>
                      </div>
                      <label className="mt-2.5 flex items-start gap-2 text-[11px] text-white/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={subConsent}
                          onChange={(e) => setSubConsent(e.target.checked)}
                          data-testid="concierge-subscribe-consent"
                          className="mt-0.5 accent-orange-500"
                        />
                        <span>Wyrażam zgodę na otrzymywanie e-maili o nowych dopasowanych ofertach. Zgodę mogę wycofać w każdej chwili.</span>
                      </label>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
